import { LoginOutlined } from "@ant-design/icons";
import { useOidc } from "@axa-fr/react-oidc";
import { environment } from "@org-avp/avp-avengers-ui-shared-environment";
import abinautPng from "@org-avp/avp-avengers-ui-shared-resources/public/abinaut.png";
import cookiePolicyPdf from "@org-avp/avp-avengers-ui-shared-resources/public/policies/Cookie_Richtlinie_DH4ViT.pdf";
import { Button, Result, Space, Spin, Typography } from "antd";
import { CSSProperties, FC, useEffect, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { OidcAppConfiguration, OidcContext } from "../../types/oidcTypes";
import { centerContainerStyle } from "../styles";

const antdResultStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  transform: "translateY(-25%)",
  background: "rgba(127, 127, 127, 0.1)",
  border: "2px solid rgba(127, 127, 127, 0.1)",
  borderRadius: "4px",
  maxWidth: "600px",
  padding: "1rem",
  width: "100%",
};

const titleStyle: CSSProperties = {
  margin: 0,
};

const extraContainerStyle: CSSProperties = {
  width: "100%",
};

const linkContainerStyle: CSSProperties = {
  display: "flex",
  alignItems: "end",
  justifyContent: "space-between",
};

const dataProtectionStyle: CSSProperties = {
  textAlign: "left",
};

const dataPolicyStyle: CSSProperties = {
  textAlign: "right",
};

export interface LoginViewProps {
  oidcAppConfiguration: OidcAppConfiguration;
}

export const DefaultLoginView: FC<LoginViewProps> = ({ oidcAppConfiguration }) => {
  if (oidcAppConfiguration.oidcContext !== OidcContext.DEFAULT) {
    throw new Error(`Expected default OIDC context, got: ${oidcAppConfiguration.oidcContext}`);
  }

  useTranslation();
  const navigate = useNavigate();
  const loginNavigationFn = useMemo(
    () => () => navigate("/"),
    [navigate],
  );

  const { isAuthenticated } = useOidc(oidcAppConfiguration.oidcContext);
  useEffect(
    () => {
      if (isAuthenticated) {
        loginNavigationFn();
      }
    },
    [isAuthenticated, loginNavigationFn],
  );

  return (
    <div style={centerContainerStyle}>
      <Result
        icon={<img src={abinautPng} alt="AVEnGeRS logo"/>}
        style={antdResultStyle}
        title={(
          <Typography.Title level={2} style={titleStyle}>
            <Trans ns="common" i18nKey="oidc.loginView.title"/>
          </Typography.Title>
        )}
        extra={(
          <Space direction="vertical" size="middle" style={extraContainerStyle}>
            {!isAuthenticated && (
              <Button type="primary" icon={<LoginOutlined/>} onClick={loginNavigationFn}>
                <Trans ns="common" i18nKey="oidc.loginView.loginButton"/>
              </Button>
            )}
            {isAuthenticated && (
              <Space size="middle">
                <Spin size="default" spinning={true}/>
                <Trans ns="common" i18nKey="oidc.loginView.labelRedirecting"/>
              </Space>
            )}
            <div style={linkContainerStyle}>
              <div style={dataProtectionStyle}>
                <div>
                  <Button type="link" href={environment.NX_IMPRINT_DATAPROTECTION_GENERAL_URL} target="_blank" rel="noreferrer">
                    <Trans ns="common" i18nKey="oidc.loginView.dataProtectionGeneral"/>
                  </Button>
                </div>
                <div>
                  <Button type="link" href={environment.NX_IMPRINT_DATAPROTECTION_EMPLOYEES_URL} target="_blank" rel="noreferrer">
                    <Trans ns="common" i18nKey="oidc.loginView.dataProtectionEmployees"/>
                  </Button>
                </div>
                <div>
                  <Button type="link" href={environment.NX_IMPRINT_DATAPROTECTION_BUSINESSPARTNERS_URL} target="_blank" rel="noreferrer">
                    <Trans ns="common" i18nKey="oidc.loginView.dataProtectionBusinessPartners"/>
                  </Button>
                </div>
              </div>
              <div style={dataPolicyStyle}>
                <div>
                  <Button type="link" href={cookiePolicyPdf} target="_blank" rel="noreferrer">
                    <Trans ns="common" i18nKey="oidc.loginView.cookiePolicy"/>
                  </Button>
                </div>
                <div>
                  <Button type="link" href={environment.NX_IMPRINT_VWAG_URL} target="_blank" rel="noreferrer">
                    <Trans ns="common" i18nKey="oidc.loginView.imprint"/>
                  </Button>
                </div>
              </div>
            </div>
          </Space>
        )}
      />
    </div>
  );
};
